import { create } from 'zustand';

import { ExamQuestionRepository } from '../../api/repository/exam-question';
import { UnitRepository } from '../../api/repository/unit';
import type { IztUnit } from '../../api/types';
import type { IzQuestion } from '../../question-bank/izteach';
import { userError } from '../../stores/userError';
import type { QuizBody } from './body';
import { sanitizeExamRow } from './examHtml';
import { examChildIds, examIdOf, examQuizBody, quizHasAnswers, randomExamSummary, type RandomExamSummary } from './fromExam';
import { i18n } from '../../i18n/index.ts';
import { loadQuizImages } from './images';
import { type MaybeMsg } from '../../i18n/msg';

/** How long the first paint waits for formulas before painting placeholders; late ones repaint. */
const IMAGE_WAIT_MS = 6000;

export type ExamQuizStatus = 'loading' | 'ready' | 'no-exam' | 'empty' | 'random' | 'error';

/** The fetched exam, kept raw: the body is built from it at render, so its wording follows the language. */
export interface ExamQuizSource {
  /** The unit's own title; '' when it has none. */
  title: string;
  rows: IzQuestion[];
  children: IzQuestion[] | null;
}

export interface ExamQuizEntry {
  status: ExamQuizStatus;
  source: ExamQuizSource | null;
  /** The teacher's IZ token read the answer keys, so the reveal has something to show. */
  answers: boolean;
  error: MaybeMsg | null;
  /** Set for a matrix exam that draws its questions per attempt. */
  random?: RandomExamSummary;
  /** The unit's exam, for its class results. */
  examId?: string;
}

interface ExamQuizState {
  /** By unit id: a test unit's ink is keyed by that id too. */
  entries: Record<string, ExamQuizEntry>;
  load: (unitId: string, unit: IztUnit, force?: boolean) => Promise<void>;
}

/** The unit's exam id, else the one the course's unit list names for it. */
async function resolveExamId(unitId: string, unit: IztUnit): Promise<string | null> {
  const direct = examIdOf(unit);
  if (direct) return direct;
  const course = typeof unit.course === 'string' ? unit.course : '';
  if (!course) return null;
  const units = await UnitRepository.titlesForCourse(course).catch(() => []);
  const match = units.filter((row) => String(row.id || row._id || '') === unitId)[0];
  return match ? examIdOf(match) : null;
}

/** The paintable body for an entry's source, worded in the current language; call at render. */
export function examQuizBodyOf(source: ExamQuizSource): QuizBody {
  return examQuizBody(source.title || i18n.t('quiz:source.test'), source.rows, source.children);
}

async function buildEntry(unitId: string, unit: IztUnit): Promise<ExamQuizEntry> {
  const examId = await resolveExamId(unitId, unit);
  if (!examId) return { status: 'no-exam', source: null, answers: false, error: null };
  const [open, keyed] = await Promise.all([ExamQuestionRepository.publicByExam(examId), ExamQuestionRepository.keyedByExam(examId)]);
  const answers = Boolean(keyed && keyed.length > 0);
  const rows = (answers && keyed ? keyed : open).map(sanitizeExamRow);
  const children = await ExamQuestionRepository.childrenByIds(examChildIds(rows), answers);
  const source: ExamQuizSource = { title: String(unit.title || unit.name || ''), rows, children: children ? children.map(sanitizeExamRow) : null };
  const body = examQuizBodyOf(source);
  if (body.questions.length === 0) {
    // a matrix exam stores no questions of its own; say what it draws instead of calling it empty
    const random = randomExamSummary(await ExamQuestionRepository.examById(examId));
    if (random) return { status: 'random', source: null, answers: false, error: null, random, examId };
    return { status: 'empty', source: null, answers: false, error: null };
  }
  await loadQuizImages(body.images || [], IMAGE_WAIT_MS);
  return { status: 'ready', source, answers: answers && quizHasAnswers(body), error: null, examId };
}

export const useExamQuizStore = create<ExamQuizState>((set, get) => ({
  entries: {},
  async load(unitId, unit, force = false) {
    const current = get().entries[unitId];
    if (current && !force && (current.status === 'loading' || current.status === 'ready')) return;
    const put = (entry: ExamQuizEntry) => set({ entries: { ...get().entries, [unitId]: entry } });
    put({ status: 'loading', source: null, answers: false, error: null });
    try {
      put(await buildEntry(unitId, unit));
    } catch (error) {
      put({ status: 'error', source: null, answers: false, error: userError(error) });
    }
  },
}));
